import React, { ReactNode } from 'react';

export type CategoryType = 
  | 'news' // 법무법인 내부소식
  | 'case' // 주요 판례 분석
  | 'trend' // 법률뉴스및 트렌드
  | 'column'; // 법률 칼럼 및해설

export const CATEGORY_LABEL: Record<CategoryType, string> = {
  'news': '법무법인 내부소식',
  'case': '주요 판례 분석',
  'trend': '법률뉴스및 트렌드',
  'column': '법률 칼럼'
};

export const CATEGORY_SHORT_LABEL: Record<CategoryType, string> = {
  'news': '내부소식',
  'case': '판례분석',
  'trend': '법률뉴스',
  'column': '법률칼럼'
};

export const CATEGORY_INITIALS: Record<CategoryType, string> = {
  'news': '내',
  'case': '판',
  'trend': '뉴',
  'column': '칼'
};

// 파스텔톤 색상을 더 은은하게 수정
export const CATEGORY_COLORS: Record<CategoryType, { 
  bg: string, 
  text: string, 
  border: string, 
  icon: string, 
  gradientFrom: string, 
  gradientTo: string,
  pastel: string,
  light: string,
  accent: string,
  elegant: string,
  elegantText: string
}> = {
  'news': { 
    bg: 'bg-blue-50', 
    text: 'text-blue-700', 
    border: 'border-blue-200',
    icon: 'text-blue-500',
    gradientFrom: 'from-blue-400',
    gradientTo: 'to-sky-300',
    pastel: 'bg-[#edf3fb]',  // 톤 다운
    light: 'bg-[#f8fbff]',
    accent: 'bg-[#4a90e2]',
    elegant: 'bg-[#fbfdff]', // 거의 흰색
    elegantText: 'text-[#3172b9]'  // 더 어두운 텍스트
  },
  'case': { 
    bg: 'bg-purple-50', 
    text: 'text-purple-700', 
    border: 'border-purple-200',
    icon: 'text-purple-500',
    gradientFrom: 'from-purple-400',
    gradientTo: 'to-violet-300',
    pastel: 'bg-[#f5f0fa]',  // 톤 다운
    light: 'bg-[#fcf9ff]',
    accent: 'bg-[#9b59b6]',
    elegant: 'bg-[#fdfbff]', // 거의 흰색
    elegantText: 'text-[#64408f]'  // 더 어두운 텍스트
  },
  'trend': { 
    bg: 'bg-amber-50', 
    text: 'text-amber-700', 
    border: 'border-amber-200',
    icon: 'text-amber-500',
    gradientFrom: 'from-amber-300',
    gradientTo: 'to-yellow-200',
    pastel: 'bg-[#faf6e8]',  // 톤 다운
    light: 'bg-[#fffdf7]',
    accent: 'bg-[#f39c12]',
    elegant: 'bg-[#fefdf9]', // 거의 흰색
    elegantText: 'text-[#b37309]'  // 더 어두운 텍스트
  },
  'column': { 
    bg: 'bg-rose-50', 
    text: 'text-rose-700', 
    border: 'border-rose-200',
    icon: 'text-rose-500',
    gradientFrom: 'from-rose-300',
    gradientTo: 'to-pink-200',
    pastel: 'bg-[#faedef]',  // 톤 다운
    light: 'bg-[#fff8f8]',
    accent: 'bg-[#e74c3c]',
    elegant: 'bg-[#fffcfc]', // 거의 흰색
    elegantText: 'text-[#b83c38]'  // 더 어두운 텍스트
  }
};

// 카테고리별 고정 설명 텍스트
export const CATEGORY_DESCRIPTIONS: Record<CategoryType, string> = {
  'news': '법무법인의 최신 소식과 성과, 구성원 동정을 확인하실 수 있습니다.',
  'case': '법률 전문가들이 분석한 중요 판례와 결정례를 제공합니다.',
  'trend': '국내외 주요 법률 이슈와 최신 법률 동향을 알려드립니다.',
  'column': '법률 전문가들의 심층적인 견해와 전문 지식을 담은 칼럼을 만나보세요.'
};

// 최신식 아이콘으로 업데이트
export const CategoryIcons: Record<CategoryType, ReactNode> = {
  'news': (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 19H5C3.89543 19 3 18.1046 3 17V7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 10H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 14H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19 15L17.5 14.5V18.5L19 18V15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="15" cy="16.5" r="2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'case': (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="14" y="4" width="4" height="8" rx="1" transform="rotate(45 14 4)" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 8L7 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 13L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="7" y="16" width="6" height="2" rx="0.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 18V20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'trend': (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 9H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 13H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 17H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19 5V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19 6L21 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M19 9L21 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  'column': (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 14V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 14V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 14V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M4 11H20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M18 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V6C20 4.89543 19.1046 4 18 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 7H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
};

interface CategoryCardProps {
  category: CategoryType;
  width?: string;
  height?: string;
  variant?: 'default' | 'minimal' | 'stylized' | 'gradient' | 'pattern' | 'initial' | 'geometric' | 'pastel' | 'modern' | 'icon' | 'elegant';
  children?: ReactNode;
  className?: string;
  contentPreview?: string;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  width = 'w-full',
  height = 'h-48',
  variant = 'default',
  children,
  className = '',
  contentPreview = '',
}) => {
  // 기본 색상 설정
  const colors = CATEGORY_COLORS[category];
  const initials = CATEGORY_INITIALS[category];
  const label = CATEGORY_LABEL[category];
  const shortLabel = CATEGORY_SHORT_LABEL[category];
  const icon = CategoryIcons[category];
  const description = CATEGORY_DESCRIPTIONS[category]; // 카테고리별 설명 텍스트

  // 첫 글자 추출 (콘텐츠 미리보기가 있는 경우)
  const firstLetter = contentPreview?.trim().charAt(0) || initials;
  
  // 패턴 배경 매핑
  const patternMap: Record<CategoryType, string> = {
    'news': `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%234c6ef5' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
    'case': `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238b5cf6' fill-opacity='0.1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    'trend': `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    'column': `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f43f5e' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
  };

  // 변형에 따른 렌더링
  const renderVariant = () => {
    switch (variant) {
      case 'minimal':
        return (
          <div className={`${width} ${height} flex flex-col items-center justify-center ${colors.bg} rounded-lg ${className}`}>
            <span className={`text-4xl font-bold ${colors.text}`}>{initials}</span>
            <p className={`${colors.text} font-medium text-sm mt-2`}>{shortLabel}</p>
            <p className={`${colors.text} text-xs mt-2 px-4 text-center line-clamp-3 opacity-75`}>
              {description}
            </p>
          </div>
        );

      case 'stylized':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg ${className}`}>
            <div className={`absolute inset-0 ${colors.bg} opacity-90`}></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48Y2lyY2xlIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iLjIiIGN4PSIxMCIgY3k9IjEwIiByPSI4Ii8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <span className={`text-5xl font-bold ${colors.text} mb-2`}>{initials}</span>
              <p className={`${colors.text} font-medium`}>{shortLabel}</p>
              <p className={`${colors.text} text-xs mt-3 line-clamp-3 max-w-xs opacity-75`}>
                {description}
              </p>
            </div>
            {children}
          </div>
        );

      case 'elegant':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg shadow-sm ${className}`}>
            <div className={`absolute inset-0 ${colors.elegant}`}></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`${colors.elegantText} opacity-70 mb-3`}>
                {icon}
              </div>
              <div className="text-center px-4">
                <h3 className={`${colors.elegantText} text-base font-semibold mb-1`}>
                  {label}
                </h3>
                <div className={`w-16 h-0.5 mx-auto ${colors.accent} opacity-20 my-2`}></div>
                <p className={`${colors.elegantText} text-sm opacity-70 font-light`}>
                  {description}
                </p>
              </div>
              <div className={`absolute top-4 right-4 ${colors.elegantText} opacity-5 text-6xl font-black`}>
                {initials}
              </div>
            </div>
            {children}
          </div>
        );

      case 'gradient':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg shadow-md ${className}`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradientFrom} ${colors.gradientTo}`}></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <span className="text-6xl font-bold text-white mb-2 drop-shadow-md font-serif">{firstLetter}</span>
              <h3 className="text-white text-lg font-medium mb-1 drop-shadow-md">{shortLabel}</h3>
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mt-2">
                <p className="text-white/90 text-sm">{label}</p>
              </div>
              <p className="text-white/80 text-xs mt-3 line-clamp-3 max-w-xs px-2">
                {description}
              </p>
            </div>
            {children}
          </div>
        );

      case 'pattern':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg shadow-md ${className}`}>
            <div className={`absolute inset-0 ${colors.bg}`}></div>
            <div className="absolute inset-0" style={{ backgroundImage: patternMap[category] }}></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/10"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <span className={`text-7xl font-bold ${colors.text} mb-2 font-serif tracking-tight`}>{firstLetter}</span>
              <h3 className={`${colors.text} text-lg font-medium mb-1`}>{shortLabel}</h3>
              <p className={`${colors.text} text-xs mt-1 line-clamp-3 max-w-xs opacity-75 px-2`}>
                {description}
              </p>
            </div>
            {children && <div className="absolute inset-0">{children}</div>}
          </div>
        );
        
      case 'initial':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg ${className}`}>
            <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradientFrom} ${colors.gradientTo} opacity-90`}></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10rem] font-black text-white/20 select-none">{firstLetter}</span>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <span className="text-5xl font-bold text-white mb-3 drop-shadow-md">{firstLetter}</span>
              <h3 className="text-white text-lg font-medium drop-shadow-md">{shortLabel}</h3>
              <p className="text-white/80 text-xs mt-3 line-clamp-3 max-w-xs">
                {description}
              </p>
            </div>
            {children}
          </div>
        );
        
      case 'geometric':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg shadow-md ${className}`}>
            <div className={`absolute inset-0 ${colors.bg}`}></div>
            <div className="absolute inset-0">
              <div className={`absolute top-0 left-0 w-20 h-20 rounded-full -translate-y-1/2 -translate-x-1/2 ${colors.gradientFrom} opacity-20`}></div>
              <div className={`absolute bottom-0 right-0 w-32 h-32 rounded-full translate-y-1/3 translate-x-1/3 ${colors.gradientTo} opacity-20`}></div>
              <div className={`absolute top-1/3 right-10 w-16 h-16 rounded-lg rotate-45 ${colors.gradientFrom} opacity-10`}></div>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <div className={`w-20 h-20 flex items-center justify-center rounded-lg ${colors.border} bg-white/80 backdrop-blur-sm shadow-lg mb-3`}>
                <span className={`text-4xl font-bold ${colors.text}`}>{firstLetter}</span>
              </div>
              <h3 className={`${colors.text} text-lg font-medium`}>{shortLabel}</h3>
              <p className={`${colors.text} text-xs mt-2 line-clamp-3 max-w-xs opacity-75 px-4`}>
                {description}
              </p>
            </div>
            {children && <div className="absolute inset-0">{children}</div>}
          </div>
        );

      case 'pastel':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg shadow-sm ${className}`}>
            <div className={`absolute inset-0 ${colors.pastel}`}></div>
            <div className="absolute inset-0 flex p-5">
              <div className="flex flex-col justify-between h-full w-full">
                <div className="flex items-center">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${colors.accent}`}>
                    <span className="text-white text-lg font-bold">{initials}</span>
                  </div>
                  <h3 className={`${colors.text} text-base font-semibold ml-2`}>
                    {shortLabel}
                  </h3>
                </div>
                <div className="flex items-end justify-between">
                  <div className={`${colors.text} text-3xl font-bold opacity-20`}>
                    {firstLetter}
                  </div>
                  <div className={`${colors.icon} opacity-80`}>
                    {icon}
                  </div>
                </div>
                <div className={`mt-3 ${colors.text} text-xs opacity-75 overflow-hidden line-clamp-3`}>
                  {description}
                </div>
              </div>
            </div>
            {children}
          </div>
        );
      
      case 'modern':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-2xl shadow-md ${className}`}>
            <div className={`absolute inset-0 ${colors.light}`}></div>
            <div className={`absolute bottom-0 left-0 right-0 h-2 ${colors.accent}`}></div>
            <div className={`absolute top-0 left-5 -translate-y-1/2 w-16 h-16 rounded-full ${colors.pastel} border-4 border-white flex items-center justify-center`}>
              <div className={`${colors.icon}`}>
                {icon}
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col justify-end p-4 pt-10">
              <h3 className={`${colors.text} text-lg font-bold`}>{shortLabel}</h3>
              <p className={`text-sm text-gray-500 mt-1 mb-3`}>{label}</p>
              <p className={`text-xs ${colors.text} opacity-75 line-clamp-3 max-w-xs mt-1`}>
                {description}
              </p>
              <div className={`text-6xl ${colors.text} font-bold opacity-10 absolute bottom-3 right-3`}>
                {firstLetter}
              </div>
            </div>
            {children}
          </div>
        );
      
      case 'icon':
        return (
          <div className={`${width} ${height} relative overflow-hidden rounded-lg ${className}`}>
            <div className={`absolute inset-0 ${colors.pastel}`}></div>
            <div className="absolute inset-0 p-4 flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div className={`${colors.text} px-3 py-1 rounded-full text-sm font-medium ${colors.light}`}>
                  {shortLabel}
                </div>
                <div className={`${colors.icon}`}>
                  {icon}
                </div>
              </div>
              <div className="text-center mt-2">
                <span className={`text-6xl font-bold ${colors.text} opacity-75`}>
                  {firstLetter}
                </span>
              </div>
              <div className={`mt-2 ${colors.text} text-xs opacity-75 line-clamp-3`}>
                {description}
              </div>
            </div>
            {children}
          </div>
        );

      default:
        return (
          <div className={`${width} ${height} relative overflow-hidden ${colors.bg} border ${colors.border} rounded-lg ${className}`}>
            <div className="absolute top-4 right-4">
              <span className={`text-4xl font-bold ${colors.text}`}>{firstLetter}</span>
            </div>
            <div className="absolute bottom-4 left-4 max-w-[80%]">
              <h3 className={`${colors.text} text-xl font-semibold`}>{shortLabel}</h3>
              <p className={`${colors.text} opacity-80 mt-1 text-sm`}>{label}</p>
              <p className={`${colors.text} text-xs mt-2 line-clamp-3 opacity-75`}>
                {description}
              </p>
            </div>
            {children}
          </div>
        );
    }
  };

  return renderVariant();
}; 